import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { type Control, useWatch } from "react-hook-form";
import { useCustomFormContext } from "@/components/site-builder/modules/CustomFormModule/context/customFormContext";
import { generateFieldName } from "@/components/site-builder/modules/CustomFormModule/utils/generateFieldName";
import { getFormFields } from "@/components/site-builder/modules/CustomFormModule/utils/getFormFields";
import { FormBuilderFieldCode } from "@/constants/form-builder";
import { useClientConfig } from "@/context/clientConfig";
import { fetchConsentConfig } from "@/lib/client/form-builder";

export interface UseCustomFormConsentConfigProps {
  control: Control;
}

export function useCustomFormConsentConfig(
  props: UseCustomFormConsentConfigProps,
) {
  const { control } = props;

  const { formEntity } = useCustomFormContext();
  const countryFieldName = useMemo(() => {
    const fields = getFormFields(formEntity);
    const countryField = fields.find(
      (field) => field.code === FormBuilderFieldCode.CountryOfResidence,
    );

    return countryField ? generateFieldName(countryField) : "";
  }, [formEntity]);
  const countryFieldValue = useWatch({
    control,
    name: countryFieldName,
    disabled: !countryFieldName,
  });
  const { siteConfig } = useClientConfig();

  return useQuery({
    queryKey: ["form-builder:consent-config", countryFieldValue, siteConfig],
    queryFn: () =>
      fetchConsentConfig({
        countryCode: countryFieldValue,
        siteConfig,
      }),
    enabled: !!countryFieldValue,
  });
}
